import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { isChunkLoadError, reloadForNewVersion } from "@/lib/lazyWithRetry";

interface Props {
  children: ReactNode;
  /** When set, renders a compact inline fallback instead of the full-screen one. */
  inline?: boolean;
  /** Optional label shown in the inline fallback to help locate the failure. */
  label?: string;
}
interface State { hasError: boolean; error: Error | null; }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Stale lazy-chunk after a new deploy: auto-reload once instead of showing
    // the scary "Something went wrong" popup.
    if (isChunkLoadError(error)) {
      reloadForNewVersion();
      return;
    }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
    try {
      window.dispatchEvent(new CustomEvent("app:error", {
        detail: { message: error.message, stack: error.stack, componentStack: info.componentStack },
      }));
    } catch { /* noop */ }
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    // Chunk-load error: a reload is already in flight; render nothing instead
    // of the scary fallback so the user just sees a quick refresh.
    if (this.state.error && isChunkLoadError(this.state.error)) return null;

    if (this.props.inline) {
      return (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-charcoal">
                Couldn't render {this.props.label || "this section"}.
              </p>
              {this.state.error?.message && (
                <p className="mt-1 break-words font-mono text-[11.5px] text-muted-foreground">
                  {this.state.error.message}
                </p>
              )}
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" onClick={this.reset}>Try again</Button>
                <Button size="sm" variant="outline" onClick={() => window.location.reload()}>Reload</Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full rounded-2xl border border-border bg-white p-8 text-center shadow-card">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="font-display text-xl font-extrabold text-charcoal">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The page hit an unexpected error. Reloading usually fixes it.
          </p>
          {this.state.error?.message && (
            <p className="mt-3 break-words rounded-md bg-muted/60 p-2 text-left text-[11.5px] font-mono text-muted-foreground">
              {this.state.error.message}
            </p>
          )}
          <div className="mt-5 flex gap-2 justify-center">
            <Button variant="outline" onClick={this.reset}>Try again</Button>
            <Button variant="cta" onClick={() => window.location.reload()}>Reload page</Button>
          </div>
        </div>
      </div>
    );
  }
}

